<script>
import Customers from "@/services/Customers";
import TreeNode from "@/components/tabs/TreeNode.vue";

export default {
  name: "TabCustomerCloudSave",
  props: {
    customer_id: { type: [String, Number], required: true },
  },
  components: { TreeNode },
  data() {
    return {
      cloudSave: null,
      cloudSaveParsed: null,
      cloudSaveLoading: false,
      cloudSaveError: null,
      searchQuery: '',
      showOnlyMatches: false,
      rootExpanded: true,
    };
  },
  async mounted() {
    await this.loadCloudSave();
  },
  methods: {
    async loadCloudSave() {
      this.cloudSaveLoading = true;
      this.cloudSaveError = null;
      this.cloudSave = null;
      this.cloudSaveParsed = null;
      try {
        const { data } = await Customers.getCloudSave(this.customer_id);
        const payload = data && data.data ? data.data : null;
        this.cloudSave = payload;
        if (payload && payload.data !== undefined) {
          if (typeof payload.data === 'string') {
            try {
              const parsedOnce = JSON.parse(payload.data);
              this.cloudSaveParsed = this.deepParseJsonStrings(parsedOnce);
            } catch {
              this.cloudSaveParsed = null;
            }
          } else if (typeof payload.data === 'object' && payload.data !== null) {
            this.cloudSaveParsed = this.deepParseJsonStrings(payload.data);
          }
        }
      } catch (err) {
        this.cloudSaveError = err.response?.data?.error || err.message;
      } finally {
        this.cloudSaveLoading = false;
      }
    },
    deepParseJsonStrings(value, depth = 0) {
      if (depth > 4) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return this.deepParseJsonStrings(parsed, depth + 1);
        } catch {
          return value;
        }
      }
      if (Array.isArray(value)) {
        return value.map(v => this.deepParseJsonStrings(v, depth + 1));
      }
      if (value && typeof value === 'object') {
        const out = {};
        Object.keys(value).forEach(k => {
          out[k] = this.deepParseJsonStrings(value[k], depth + 1);
        });
        return out;
      }
      return value;
    },
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    highlight(text, query) {
      if (!query) return this.escapeHtml(text);
      const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(esc, 'gi');
      return this.escapeHtml(text).replace(re, m => `<mark>${m}</mark>`);
    },
    buildFilteredJson(obj, query) {
      // Return a pruned copy containing only matching branches
      if (!query) return obj;
      const q = query.toLowerCase();
      const walk = (val) => {
        if (val == null) return null;
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          return String(val).toLowerCase().includes(q) ? val : null;
        }
        if (Array.isArray(val)) {
          const arr = val.map(walk).filter(v => v !== null);
          return arr.length ? arr : null;
        }
        if (typeof val === 'object') {
          const out = {};
          Object.keys(val).forEach(k => {
            const child = walk(val[k]);
            if (child !== null || k.toLowerCase().includes(q)) {
              out[k] = child === null ? val[k] : child;
            }
          });
          return Object.keys(out).length ? out : null;
        }
        return null;
      };
      const pruned = walk(obj);
      return pruned ?? {};
    }
  }
}
</script>

<template>
  <div class="col-12">
    <div class="card">
      <div class="card-body">
        <div class="row align-items-center mb-3">
          <div class="col-sm-12 col-md-8">
            <div class="input-group">
              <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
              <input v-model="searchQuery" type="search" class="form-control" placeholder="Search in cloud data (keys and values)..." />
            </div>
          </div>
          <div class="col-sm-12 col-md-4 mt-2 mt-md-0">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="onlyMatches" v-model="showOnlyMatches">
              <label class="form-check-label" for="onlyMatches">Show only matching entries</label>
            </div>
          </div>
        </div>
        <div v-if="cloudSaveLoading" class="d-flex justify-content-center align-items-center" style="min-height: 120px;">
          <b-spinner large></b-spinner>
        </div>
        <div v-else>
          <div v-if="cloudSaveError" class="alert alert-danger">
            {{ cloudSaveError }}
          </div>
          <template v-else-if="cloudSave">
            <div class="row mb-3">
              <div class="col-sm-12 col-md-3"><strong>Customer ID:</strong> {{ cloudSave.customer_id }}</div>
              <div class="col-sm-12 col-md-3"><strong>Version:</strong> {{ cloudSave.version }}</div>
              <div class="col-sm-12 col-md-3"><strong>Updated UTC:</strong> {{ cloudSave.updated_utc }}</div>
              <div class="col-sm-12 col-md-3"><strong>Timestamp:</strong> {{ cloudSave.timestamp }}</div>
            </div>
            <div class="mb-2"><strong>Data Source:</strong> {{ cloudSave.data_source }}</div>
            <hr/>
            <div>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5 class="mb-0">Data</h5>

                <button class="btn btn-sm btn-success" @click="downloadJsonData(cloudSaveParsed, 'cloudSave_'+cloudSave.customer_id)">Download</button>
              </div>

              <div style="max-height: 480px; overflow: auto;">
                <TreeNode v-if="!showOnlyMatches" :node-val="cloudSaveParsed || {}" :path="'$'" :search="searchQuery" />
                <TreeNode v-else :node-val="buildFilteredJson(cloudSaveParsed || {}, searchQuery)" :path="'$'" :search="searchQuery" />
              </div>
            </div>
          </template>
          <div v-else class="text-center text-muted">
            <i class="uil uil-cloud font-size-24 me-2"></i>
            <span>No Cloud Save Data.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>


